import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, useFetch, useMutation } from '../api';

type GetRestrictedKeyRequests =
  ApiClient['v2']['workspaces'][':workspaceId']['applications'][':applicationId']['restricted-key-requests']['$get'];
export type RestrictedKeyRequestListResponse = InferResponseType<GetRestrictedKeyRequests, 200>;
export type RestrictedKeyRequestResponse =
  RestrictedKeyRequestListResponse['restrictedKeyRequests'][number];

type CreateRestrictedKeyRequest =
  ApiClient['v2']['workspaces'][':workspaceId']['applications'][':applicationId']['restricted-key-requests']['$post'];
export type CreateRestrictedKeyRequestRequest =
  InferRequestType<CreateRestrictedKeyRequest>['json'];
type CreateRestrictedKeyRequestResponse = InferResponseType<CreateRestrictedKeyRequest, 201>;

export function useRestrictedKeyRequests(workspaceId: number, applicationId: number) {
  return useFetch<RestrictedKeyRequestListResponse>(
    `workspaces/${workspaceId}/applications/${applicationId}/restricted-key-requests`,
    (client) =>
      client.v2.workspaces[':workspaceId'].applications[':applicationId'][
        'restricted-key-requests'
      ].$get({
        param: { workspaceId: workspaceId.toString(), applicationId: applicationId.toString() },
        query: { limit: 1000, offset: 0 },
      })
  );
}

export function useCreateRestrictedKeyRequest(
  workspaceId: number,
  applicationId: number,
  onError?: (error?: unknown) => void
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/applications/${applicationId}/restricted-key-requests`;

  const createMutation = useMutation<
    CreateRestrictedKeyRequestResponse,
    CreateRestrictedKeyRequestRequest
  >(
    listCacheKey,
    (client, data) =>
      client.v2.workspaces[':workspaceId'].applications[':applicationId'][
        'restricted-key-requests'
      ].$post({
        param: { workspaceId: workspaceId.toString(), applicationId: applicationId.toString() },
        json: data,
      }),
    onError,
    { suppressToast: true }
  );

  return useCallback(
    async (data: CreateRestrictedKeyRequestRequest) => {
      const result = await createMutation(data);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      return result;
    },
    [createMutation, mutate, listCacheKey]
  );
}
